import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildTelehealthSessionPath } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import { Video, Clock, Calendar, ArrowRight, Play, Loader2, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { NewSessionDialog } from "./NewSessionDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { displayTime, displayDate } from "@/lib/timeUtils";
import { format, isBefore, startOfDay } from "date-fns";

const statusConfig = {
  scheduled: {
    badge: "Upcoming",
    badgeClass: "bg-muted text-muted-foreground",
    showJoin: true,
  },
  waiting: {
    badge: "Waiting",
    badgeClass: "bg-amber-100 text-amber-700",
    showJoin: true,
  },
  "in_progress": {
    badge: "In Progress",
    badgeClass: "bg-success/10 text-success",
    showJoin: true,
  },
  completed: {
    badge: "Completed",
    badgeClass: "bg-muted text-muted-foreground",
    showJoin: false,
  },
  cancelled: {
    badge: "Cancelled",
    badgeClass: "bg-destructive/10 text-destructive",
    showJoin: false,
  },
};

export function TelehealthSchedule() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [pastSearch, setPastSearch] = useState("");
  const [pastDateFilter, setPastDateFilter] = useState<Date | undefined>(undefined);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: allSessions, isLoading } = useQuery({
    queryKey: ["telehealth-schedule-all", user?.id],
    queryFn: async () => {
      const { data: sessions, error } = await supabase
        .from("telehealth_sessions")
        .select(`
          *,
          member_profile:profiles!telehealth_sessions_member_id_profiles_fkey(id, display_name, avatar_url)
        `)
        .eq("provider_id", user!.id)
        .order("scheduled_date", { ascending: false })
        .order("scheduled_time", { ascending: false });

      if (error) throw error;
      if (!sessions || sessions.length === 0) return [];

      const memberIds = [...new Set(sessions.map((s) => s.member_id))];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name, patient_photo_url")
        .in("user_id", memberIds);

      return sessions.map((session: any) => {
        const intake = intakeForms?.find((i) => i.user_id === session.member_id);
        const profile = session.member_profile;
        const name = intake
          ? `${intake.first_name} ${intake.last_name}`
          : profile?.display_name || "Unknown Patient";
        const image = intake?.patient_photo_url || profile?.avatar_url || undefined;

        return {
          ...session,
          member_profile: undefined,
          patientName: name,
          patientImage: image,
        };
      });
    },
    enabled: !!user?.id,
  });

  const now = new Date();
  const todayStr = format(startOfDay(now), "yyyy-MM-dd");

  const upcomingSessions = useMemo(() => {
    return (allSessions || [])
      .filter((s) => {
        if (s.status === "cancelled") return false;
        // Future date, or today with non-completed status
        return s.scheduled_date > todayStr || (s.scheduled_date === todayStr && s.status !== "completed");
      })
      .sort((a, b) => {
        const dateCompare = a.scheduled_date.localeCompare(b.scheduled_date);
        if (dateCompare !== 0) return dateCompare;
        return a.scheduled_time.localeCompare(b.scheduled_time);
      });
  }, [allSessions, todayStr]);

  const pastSessions = useMemo(() => {
    let past = (allSessions || []).filter((s) => {
      return s.scheduled_date < todayStr || (s.scheduled_date === todayStr && (s.status === "completed" || s.status === "cancelled"));
    }).sort((a, b) => {
      const dateCompare = b.scheduled_date.localeCompare(a.scheduled_date);
      if (dateCompare !== 0) return dateCompare;
      return b.scheduled_time.localeCompare(a.scheduled_time);
    });

    // Apply search filter
    if (pastSearch.trim()) {
      const q = pastSearch.toLowerCase();
      past = past.filter((s) => s.patientName.toLowerCase().includes(q));
    }

    // Apply date filter
    if (pastDateFilter) {
      const filterStr = format(pastDateFilter, "yyyy-MM-dd");
      past = past.filter((s) => s.scheduled_date === filterStr);
    }

    return past;
  }, [allSessions, todayStr, pastSearch, pastDateFilter]);

  const formatTime = (timeString: string, dateString?: string) => {
    if (dateString) return displayTime(timeString, dateString);
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const renderSessionRow = (apt: any, index: number, showDate = false) => {
    const status = statusConfig[apt.status as keyof typeof statusConfig] || statusConfig.scheduled;
    return (
      <div
        key={apt.id}
        className={cn(
          "flex items-center justify-between rounded-lg border p-4 transition-all",
          apt.status === "in_progress"
            ? "border-success/50 bg-success/5"
            : "border-border/50 bg-muted/30 hover:border-border hover:bg-muted/50"
        )}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="flex items-center gap-4">
          <Avatar className="h-10 w-10">
            <AvatarImage src={apt.patientImage} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {apt.patientName
                .split(" ")
                .map((n: string) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-foreground">{apt.patientName}</p>
            <p className="text-sm text-muted-foreground">{apt.visit_type}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            {showDate && (
              <p className="text-xs text-muted-foreground mb-0.5">
                {displayDate(apt.scheduled_date)}
              </p>
            )}
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              {formatTime(apt.scheduled_time, apt.scheduled_date)}
            </div>
            <p className="text-xs text-muted-foreground">{apt.duration_minutes || 30} min</p>
          </div>

          <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", status.badgeClass)}>
            {status.badge}
          </span>

          {status.showJoin && apt.zoom_start_url && (
            <Button
              size="sm"
              variant="success"
              className="gap-1.5"
              onClick={() => window.open(apt.zoom_start_url, "_blank")}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Start Zoom
            </Button>
          )}

          {status.showJoin && !apt.zoom_start_url && (
            <Button
              size="sm"
              variant={apt.status === "in_progress" ? "success" : "outline"}
              className="gap-1.5"
              onClick={() => navigate(buildTelehealthSessionPath(apt.id))}
            >
              <Play className="h-3.5 w-3.5" />
              {apt.status === "in_progress" ? "Rejoin" : "Join"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-card animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-chart-5/10 p-2">
            <Video className="h-5 w-5 text-chart-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Telehealth Sessions</h3>
            <p className="text-sm text-muted-foreground">
              {upcomingSessions.length} upcoming session{upcomingSessions.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate(ROUTES.PROVIDER_SCHEDULE)}>
          Full Calendar <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="upcoming" className="flex-1">Upcoming</TabsTrigger>
          <TabsTrigger value="past" className="flex-1">Past Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : upcomingSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No upcoming sessions</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {upcomingSessions.map((apt, i) => renderSessionRow(apt, i, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-0">
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by patient name..."
                value={pastSearch}
                onChange={(e) => setPastSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn("gap-2", pastDateFilter && "text-primary")}>
                  <Calendar className="h-4 w-4" />
                  {pastDateFilter ? format(pastDateFilter, "MMM d, yyyy") : "Filter by date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarPicker
                  mode="single"
                  selected={pastDateFilter}
                  onSelect={setPastDateFilter}
                  disabled={(d) => !isBefore(d, startOfDay(new Date()))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {pastDateFilter && (
              <Button variant="ghost" size="sm" onClick={() => setPastDateFilter(undefined)}>
                Clear
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : pastSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No past sessions found</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {pastSessions.map((apt, i) => renderSessionRow(apt, i, true))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Button className="mt-6 w-full" variant="gradient" onClick={() => setDialogOpen(true)}>
        <Calendar className="mr-2 h-4 w-4" />
        Schedule New Session
      </Button>

      <NewSessionDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
